import {Box, Button, Modal, Typography} from '@viptech/react-components'
import {Buffer} from 'buffer'
import {useSnackbar} from 'notistack'
import {useEffect, useState} from 'react'
import {BtnClose} from 'src/components/BtnClose'
import Dropdown from 'src/components/dropdown/Dropdown'
import {DropdownItem} from 'src/components/dropdown/DropdownProps'
import {Input} from 'src/components/input/Input'
import useCompanies from 'src/hooks/api/companies/useCompanies'
import useCreateServer from 'src/hooks/api/server/useCreateServer'
import useTestConnection from 'src/hooks/api/server/useTestConnection'
import useUpdatedServer from 'src/hooks/api/server/useUpdatedServer'
import {EnumServer, Server} from 'src/services/api'
import {isValidHostAddress} from 'src/utilities/regexValidation'
import styled from 'styled-components'

type ServerRegisterModalProps = {
  isCreate: boolean
  openModal: boolean
  closeModal: () => void
  server: Server
  setServer: (server: Server) => void
  loadServers: Function
}

export type AuthServer = {
  user: string | undefined
  password: string | undefined
}

const ServersInputOptions = [
  {id: '2', label: 'Digifort'},
  {id: '4', label: 'D-Guard'},
]

const ServersOutputOptions = [
  {id: '1', label: 'Sigma-Cloud'},
  {id: '3', label: 'Moni'},
  {id: '6', label: 'One Portaria'},
]

function ServerRegisterModal(props: ServerRegisterModalProps) {
  const {isCreate, openModal, closeModal, server, setServer, loadServers} = props
  const {enqueueSnackbar} = useSnackbar()

  const [basicAuth, setBasicAuth] = useState<AuthServer>({user: undefined, password: undefined})

  const [sigmaToken, setSigmaToken] = useState<string>('')

  const [host, setHost] = useState<string>('')
  const [port, setPort] = useState<string>('')
  const [protocol, setProtocol] = useState<DropdownItem>()

  const urlFull = window.location.href

  const {data: companies, isLoading: loadingCompany} = useCompanies()
  const [companySelected, setCompanySelected] = useState<DropdownItem>()

  const [serverTypeSelected, setServerTypeSelected] = useState<DropdownItem>()

  const {mutateAsync: testConnection, isPending: testLoading} = useTestConnection()

  useEffect(() => {
    const inputPort = port ? `:${port}` : ''
    setServer({...server, ip: `${protocol?.id}://${host}${inputPort}`})
  }, [protocol, host, port])

  useEffect(() => {
    const handleEdit = () => {
      const linkSeparated = server.ip.split(':')
      if (linkSeparated.length >= 2) {
        const protocol = linkSeparated[0]
        const hostOrIp = linkSeparated[1].split('//')[1]
        const port = linkSeparated[2]

        setProtocol({id: protocol, label: protocol.toUpperCase()})
        setHost(hostOrIp)
        setPort(port)
      }

      if (server.companyId) {
        const company = companies?.pages.flatMap((it) => it.entities.find((companie) => companie.id === server.companyId))
        if (company && company[0]) setCompanySelected({id: String(company[0].id), label: company[0].name})
      }

      if (server.serverTypeId) {
        const allServersTypes = [...ServersInputOptions, ...ServersOutputOptions]
        const serverSelected = allServersTypes.find((s) => Number(s.id) === server.serverTypeId)
        setServerTypeSelected(serverSelected)
      }
    }
    handleEdit()
  }, [isCreate])

  const handleAuth = () => {
    const {user, password} = basicAuth
    if (user && password) {
      if (server.serverType?.id === EnumServer.D_GUARD) return `${user}:${password}`
      return base64converter(`${user}:${password}`)
    }
    if (sigmaToken && server.serverType?.id === EnumServer.SIGMA_CLOUD) return sigmaToken
  }

  function base64converter(string: string) {
    const encodedString = Buffer.from(string).toString('base64')
    return encodedString
  }

  const {mutateAsync: createServer, isPending: isLoadingCreate} = useCreateServer()
  const {mutateAsync: updateServer, isPending: isLoadingUpdate} = useUpdatedServer()

  const handleSaveAction = async () => {
    const auth = handleAuth()

    if (isCreate) {
      if (!auth) return enqueueSnackbar('Não foi possível gerar as crendenciais de acesso', {variant: 'warning'})
      await createServer({
        name: server.name,
        ip: server.ip,
        serverTypeId: Number(serverTypeSelected?.id),
        auth,
        companyId: Number(companySelected?.id),
      })
    } else await updateServer({id: server.id, name: server.name, ip: server.ip, serverTypeId: server.serverType?.id, auth})
    closeModal()
    loadServers()
  }

  const handleDisableConfirmButton = () => {
    if (server.name === '' || !isValidHostAddress(host) || !server.serverType?.id) return true
    const hasAuth = server.serverType.id !== EnumServer.SIGMA_CLOUD ? basicAuth.password && basicAuth.user : sigmaToken.trim()
    if (isCreate && !hasAuth) return true
    else if (!isCreate && ((!basicAuth.password && basicAuth.user) || (basicAuth.password && !basicAuth.user))) return true
    return false
  }

  const [showAuth, setShowAuth] = useState(isCreate === true)

  return (
    <Modal isOpen={openModal} closeModal={closeModal} backgroundColor='white' width='700px'>
      <Box padding='30px' display='flex' direction='column' rowGap='30px'>
        <Box display='flex' align='center'>
          <Typography variant='p' size='20px' color='#344054' children={isCreate ? 'Adicionar novo servidor' : 'Editar servidor'}></Typography>
          <BtnClose onClose={closeModal} />
        </Box>

        <Box display='flex' direction='column' rowGap='15px'>
          <Input.Root
            labelFontSize='14px'
            borderColor='#D0D5DD'
            fontSize='12px'
            controlled
            label='Nome da integração'
            required
            fontWeight='400'
            labelColor='#55565b'
            value={server.name}
            onChange={(e) => setServer({...server, name: e.target.value.substring(0, 99)})}
          />

          <Dropdown
            label='Tipo da integração'
            items={urlFull.includes('inputs') ? ServersInputOptions : ServersOutputOptions}
            selected={serverTypeSelected}
            fontSize='12px'
            required
            borderColor='#D0D5DD'
            disabled={!isCreate}
            onChangeSelected={(e) => {
              if (Number(e.id) === EnumServer.SIGMA_CLOUD) {
                setProtocol({
                  id: 'https',
                  label: 'HTTPS',
                })
                setPort('')
                setHost('api.segware.com.br')
              }
              setServer({...server, serverType: {id: Number(e.id)}})
              setServerTypeSelected(e)
            }}
          />

          <Dropdown
            label='Empresa'
            fontSize='12px'
            borderColor='#D0D5DD'
            isLoading={loadingCompany}
            required
            items={
              companies?.pages.flatMap((it) =>
                it.entities.map((company) => {
                  return {
                    id: String(company.id),
                    label: company.name,
                  }
                }),
              ) ?? []
            }
            selected={companySelected}
            disabled={!isCreate}
            onChangeSelected={(e) => {
              setCompanySelected(e)
            }}
          />

          <Box display='flex' columnGap='15px'>
            <Dropdown
              label='Protocolo'
              items={[
                {
                  id: 'http',
                  label: 'HTTP',
                },
                {
                  id: 'https',
                  label: 'HTTPS',
                },
              ]}
              fontSize='12px'
              required
              borderColor='#D0D5DD'
              selected={protocol}
              disabled={server.serverType?.id === EnumServer.SIGMA_CLOUD}
              onChangeSelected={(e) => {
                setProtocol(e)
              }}
              width='300px'
              placeholder=''
            />

            <Input.Root
              labelFontSize='14px'
              borderColor='#D0D5DD'
              fontSize='12px'
              controlled
              label='Host'
              placeholder='000.000.000.000'
              required
              fontWeight='400'
              labelColor='#55565b'
              value={host}
              disabled={server.serverType?.id === EnumServer.SIGMA_CLOUD}
              onChange={(e) => setHost(e.target.value)}
            />

            <Input.Root
              labelFontSize='14px'
              borderColor='#D0D5DD'
              fontSize='12px'
              controlled
              label='Porta'
              placeholder='0000'
              required
              fontWeight='400'
              labelColor='#55565b'
              value={port}
              disabled={server.serverType?.id === EnumServer.SIGMA_CLOUD}
              onChange={(e) => setPort(e.target.value.replace(/[^0-9]+/, '').substring(0, 5))}
              width='400px'
            />
          </Box>
        </Box>

        {[EnumServer.DIGIFORT, EnumServer.MONI, EnumServer.D_GUARD, EnumServer.ONE_PORTARIA].find((it) => it === server.serverType?.id) && (
          <Box display='flex' direction='column' rowGap='10px'>
            <Box display='flex' justifyContent='space-between' align='center' width='100%'>
              <Box display='flex' columnGap='10px' align='center'>
                <Typography variant='p' size='16px' weight={500} children='Autenticação' />
                {!isCreate && (
                  <Button
                    variant='outlined'
                    onClick={() => {
                      setShowAuth((prev) => {
                        if (!!prev) setBasicAuth({user: undefined, password: undefined})
                        return !prev
                      })
                    }}
                    fontSize='12px'
                    height='30px'>
                    {!showAuth ? 'Editar' : 'Cancelar'}
                  </Button>
                )}
              </Box>
              {server.serverType?.id !== EnumServer.ONE_PORTARIA && (
                <Link
                  onClick={() => {
                    if (server.serverType?.id === EnumServer.MONI) window.open(`/tutorials/moni`)
                    else if (server.serverType?.id === EnumServer.D_GUARD) window.open(`/tutorials/dguard`)
                    else if (server.serverType?.id === EnumServer.DIGIFORT) window.open(`/tutorials/digifort`)
                  }}>
                  Como integrar?
                </Link>
              )}
            </Box>

            {showAuth && (
              <>
                <Input.Root
                  labelFontSize='14px'
                  borderColor='#D0D5DD'
                  fontSize='12px'
                  controlled
                  label='Usuário'
                  required
                  fontWeight='400'
                  labelColor='#55565b'
                  value={basicAuth.user ?? ''}
                  onChange={(e) => setBasicAuth({...basicAuth, user: e.target.value.substring(0, 99)})}
                />

                <Input.Root
                  labelFontSize='14px'
                  borderColor='#D0D5DD'
                  fontSize='12px'
                  controlled
                  label='Senha'
                  type='password'
                  required
                  fontWeight='400'
                  labelColor='#55565b'
                  value={basicAuth.password ?? ''}
                  onChange={(e) => setBasicAuth({...basicAuth, password: e.target.value.substring(0, 99)})}
                />
              </>
            )}
          </Box>
        )}

        {server.serverType?.id === EnumServer.SIGMA_CLOUD && urlFull.includes('outputs') && (
          <Input.Root
            labelFontSize='14px'
            borderColor='#D0D5DD'
            fontSize='12px'
            controlled
            label='Token'
            required
            fontWeight='400'
            labelColor='#55565b'
            value={sigmaToken}
            onChange={(e) => setSigmaToken(e.target.value)}
          />
        )}

        <Box display='flex' width='100%' columnGap='10px' justifyContent='space-between'>
          <Button
            variant='contained'
            isLoading={testLoading}
            onClick={() =>
              testConnection({
                ip: server.ip ? server.ip : 'api.segware.com.br',
                auth: handleAuth(),
                serverType: server.serverType?.id!,
                id: server.id === 0 ? undefined : server.id,
              })
            }
            height='38px'
            width={showAuth ? '220px' : '170px'}
            fontSize='12px'>
            {showAuth ? 'Testar nova credencial' : 'Testar Integração'}
          </Button>

          <Box display='flex' width='100%' columnGap='10px' justifyContent='flex-end'>
            <Button variant='outlined' fontSize='12px' height='38px' width='120px' color='#8E8E8E' onClick={closeModal}>
              Cancelar
            </Button>
            <Button
              variant='contained'
              fontSize='12px'
              height='38px'
              width='120px'
              disabledTextColor='#8E8E8E'
              onClick={() => handleSaveAction()}
              isLoading={isLoadingCreate || isLoadingUpdate}
              disabled={handleDisableConfirmButton()}>
              {isCreate ? 'Adicionar' : 'Salvar'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default ServerRegisterModal

const Link = styled.a`
  font-size: 14px;
  font-weight: 600;
  color: ${({theme}) => theme.colors.primary};
  cursor: pointer;

  :hover {
    color: ${({theme}) => theme.hoverColors.primary};
  }
`
